import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import usePageApi from "hooks/api/private/usePageApi";
import useSnackbar from "hooks/useSnackbar";

import { PageListItem } from "types/api/page";

export type PageListHook = {
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    navigateToNewPage: () => void;
    alertConfirmHook: AlertConfirmHookType;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<PageListItem>;
};

function usePagesList(): PageListHook {
    const { getAllPage, deletePage } = usePageApi();

    const navigate = useNavigate();

    const navigateToEditPage = React.useCallback(
        (row: PageListItem) => {
            navigate(makeUrl(":pageId/modifica", { pageId: row.id }));
        },
        [navigate]
    );

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        []
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "page-list-search",
        memoizedFieldsForSearch
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;

                return newForm;
            }),
        [setFiltersForm]
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Pagina",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deletePageButton = React.useCallback(
        (row: PageListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la pagina?", () => {
                const [apiCall] = deletePage({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Pagina eliminata con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione della pagina"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deletePage,
        ]
    );

    const columns: Array<Column<PageListItem>> = React.useMemo(
        (): Array<Column<PageListItem>> => [
            {
                id: "name",
                header: "Nome",
                orderable: true,
                render: (row: PageListItem) => row.name,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: PageListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditPage}
                        onDelete={deletePageButton}
                    />
                ),
            },
        ],
        [navigateToEditPage, deletePageButton]
    );

    const dataTableHook = useDataTableWithMemory<PageListItem>({
        apiCall: getAllPage,
        getElements: calculateRows,
        columns: columns,
        size: 20,
        filters: filters,
        key: "page-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewPage = React.useCallback(() => {
        navigate("/pagine/nuova");
    }, [navigate]);

    return React.useMemo(
        (): PageListHook => ({
            onSearchFilterChange,
            navigateToNewPage,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        }),
        [navigateToNewPage, onSearchFilterChange, alertConfirmHook, dataTableHook, filterSearchHook]
    );
}

export default usePagesList;
